import React, { useState, useEffect, useMemo } from 'react'
import { Box, Skeleton, Typography } from '@mui/material'
import { useDispatch, useSelector } from 'react-redux'
import { skipToken } from '@reduxjs/toolkit/query'
import { FormProvider } from 'react-hook-form'

import BasicTabs from 'components/tabs/basic-tabs/basic-tabs.component'
import TabPanel from 'components/tabs/tab-panel/tab-panel.component'
import OrderTracking from 'components/order-tracking/order-tracking.component'
import ActionButtons from 'components/action-buttons/action-buttons.component'

import { RootState } from 'store'
import { Order } from 'store/orders/orders.types'
import {
  useDeleteOrderMutation,
  useLazyListNewOrdersQuery,
  useLazyListActiveOrdersQuery,
  useLazyListArchivedOrdersQuery,
  useUpdateOrderStatusMutation,
} from 'store/orders/orders.api'
import ConfirmationDialog from 'components/confirmation-dialog/confirmation-dialog.component'
import { clearSelectedOrder } from 'store/orders/orders.slice'

import OrderNotSelected from './order-not-selected/order-not-selected.component'
import { verifyOrderTabs } from '../../verify-order/verify-order.constant'
import LoadCustomerForm from 'components/forms/load-customer-form/load-customer-form.component'
import LoadRateForm from 'components/forms/load-rate-form/load-rate-form.component'
import LoadPickupForm from 'components/forms/load-pickup-form/load-pickup-form.component'
import LoadDeliveryForm from 'components/forms/load-delivery-form/load-delivery-form.component'
import LoadNotesForm from 'components/forms/load-notes-form/load-notes-form.component'
import { useVerifyOrderForm } from '../../verify-order/verify-order-form/verify-order-form.hooks'
import { EditModeProvider, useEditMode } from 'contexts/edit-mode.context'
import { formatPhoneForDisplay } from 'utils/phone'
import { getRole } from 'utils/common'

const OrderDetailsSkeleton = () => (
  <Box
    sx={{
      display: 'flex',
      flex: '1 1 0%',
      height: '100%',
      minHeight: 0,
      overflow: 'hidden',
    }}
  >
    <Box sx={{ flex: 1, p: 2.5, minWidth: 0 }}>
      <Skeleton variant="rounded" height={36} width={320} sx={{ mb: 3 }} />
      {Array.from({ length: 8 }).map((_, index) => (
        <Box
          key={index}
          sx={{
            display: 'grid',
            gridTemplateColumns: '168px minmax(0, 1fr)',
            columnGap: 2.5,
            alignItems: 'center',
            mb: 2.5,
          }}
        >
          <Skeleton variant="text" width={120} height={24} />
          <Skeleton variant="rounded" height={40} />
        </Box>
      ))}
    </Box>
    <Box
      sx={{
        width: '34%',
        minWidth: 260,
        borderLeft: '1px solid',
        borderColor: 'divider',
        bgcolor: 'secondary.light',
        p: 2,
      }}
    >
      <Skeleton variant="text" width={100} height={28} sx={{ mb: 2 }} />
      <Skeleton variant="rounded" height={120} sx={{ mb: 2 }} />
      <Skeleton variant="rounded" height={120} />
    </Box>
  </Box>
)

// Default order constant to avoid recreating on every render
const defaultOrder: Order = {
  orderId: 0,
  userOrderId: '',
  status: 'NEW',
  loadNumber: '',
  companyId: '',
  customerId: '',
  customerName: '',
  notificationEmail: '',
  billingEmail: '',
  baseRate: 0,
  detentionCharges: 0,
  layoverCharges: 0,
  fuelSurcharge: 0,
  otherCharges: 0,
  notes: '',
  accountPayableEmail: '',
  currency: 'CAD',
  orderEvents: [],
}

const OrderDetailsContent = () => {
  const [tabsValue, setTabsValue] = useState(0)
  const { selectedOrder, selectedCompany, selectedOrderTab, isSelectedOrderLoading } =
    useSelector((state: RootState) => state.orders)
  const role = getRole()
  const isDispatch = role === 'DISPATCH'
  const isPendingLcDoc = selectedOrder?.isTempLcDoc
  const [deleteOpen, setDeleteOpen] = useState(false)
  const { setIsEditMode } = useEditMode()

  const dispatch = useDispatch()

  // Merge order and company data for form
  const orderForForm = useMemo(() => {
    if (!selectedOrder) return defaultOrder

    // Build address from company data
    const address = selectedCompany
      ? `${selectedCompany.streetNumber || ''} ${selectedCompany.streetName || ''} ${selectedCompany.city || ''} ${selectedCompany.state || ''}`.trim()
      : ''

    return {
      ...selectedOrder,
      address: address,
      // Customer name from company details (company API); order and company share same name, different DB columns
      customerName: selectedCompany?.companyName ?? selectedOrder.customerName ?? '',
      notificationEmail: selectedCompany?.notificationEmail || '',
      billingEmail: selectedCompany?.invoicePodEmail || '',
      accountPayableEmail: selectedCompany?.accountPayableEmail || '',
      phone: formatPhoneForDisplay(selectedCompany?.phoneNumber || ''),
      currency: selectedOrder.currency || 'CAD',
    }
  }, [selectedOrder, selectedCompany])

  const formMethods = useVerifyOrderForm(orderForForm)

  // Reset form and edit mode when order details finish loading
  useEffect(() => {
    if (!selectedOrder || selectedOrder.orderId === 0) return
    // Wait until full order/company fetch completes so rate fields are populated
    if (isSelectedOrderLoading) return

    const address = selectedCompany
      ? `${selectedCompany.streetNumber || ''} ${selectedCompany.streetName || ''} ${selectedCompany.city || ''} ${selectedCompany.state || ''}`.trim()
      : ''

    // Convert orderEvents to match form schema (numbers to strings)
    const formattedOrderEvents = (selectedOrder.orderEvents || []).map((event) => ({
      startTime: event.startTime || '',
      endTime: event.endTime || '',
      name: event.name || '',
      address: event.address || '',
      loadType: event.loadType || '',
      loadCount: event.loadCount?.toString() || '',
      temperatureValue: event.temperatureValue?.toString() || '',
      hazmat: event.hazmat || '',
      pickupNumber: event.pickupNumber || '',
      weightValue: event.weightValue?.toString() || '',
      notes: event.notes || '',
    }))

    formMethods.reset({
      customerName: selectedCompany?.companyName ?? selectedOrder.customerName ?? '',
      address: address,
      loadNumber: selectedOrder.loadNumber || '',
      billingEmail: selectedCompany?.invoicePodEmail || '',
      notificationEmail: selectedCompany?.notificationEmail || '',
      accountPayableEmail: selectedCompany?.accountPayableEmail || '',
      phone: formatPhoneForDisplay(selectedCompany?.phoneNumber || ''),
      other: '',
      currency: selectedOrder.currency || 'CAD',
      baseRate: selectedOrder.baseRate?.toString() || '',
      detentionCharges: selectedOrder.detentionCharges?.toString() || '',
      layoverCharges: selectedOrder.layoverCharges?.toString() || '',
      fuelSurcharge: selectedOrder.fuelSurcharge?.toString() || '',
      otherCharges: selectedOrder.otherCharges?.toString() || '',
      notes: selectedOrder.notes || '',
      orderEvents: formattedOrderEvents,
    })
    setIsEditMode(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedOrder?.orderId,
    selectedCompany?.id,
    isSelectedOrderLoading,
    formMethods.reset,
    setIsEditMode,
  ])

  const [deleteOrderById, { isError }] = useDeleteOrderMutation()
  const [updateOrderStatus] = useUpdateOrderStatusMutation()
  const [refetchNewOrders] = useLazyListNewOrdersQuery()
  const [refetchActiveOrders] = useLazyListActiveOrdersQuery()
  const [refetchArchiveOrders] = useLazyListArchivedOrdersQuery()

  const listParams = { page: 0, size: 10 }

  const refetchCurrentTabOrders = () => {
    if (selectedOrderTab === 'NEW') {
      void refetchNewOrders(listParams)
    } else if (
      selectedOrderTab === 'ASSIGNED' ||
      selectedOrderTab === 'ACTIVE'
    ) {
      void refetchActiveOrders(listParams)
    } else if (
      selectedOrderTab === 'DELIVERED' ||
      selectedOrderTab === 'ARCHIVED'
    ) {
      void refetchArchiveOrders(listParams)
    }
  }

  const handleChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabsValue(newValue)
  }

  const handleMarkAsCompleted = async () => {
    try {
      await updateOrderStatus({
        orderId: selectedOrder.orderId,
        status: 'ARCHIVED',
      }).unwrap()
      void refetchActiveOrders(listParams)
      void refetchArchiveOrders(listParams)
      dispatch(clearSelectedOrder())
    } catch (error) {
      console.error('Error while marking order as completed', error)
    }
  }

  const handleDeleteOrder = async () => {
    try {
      await deleteOrderById({
        orderId: selectedOrder.orderId.toString() ?? skipToken,
      })

      if (!isError) {
        refetchCurrentTabOrders()
        setDeleteOpen(false)
        dispatch(clearSelectedOrder())
      }
    } catch (error) {
      console.error('Error while deleting order', error)
    }
  }

  // Calculate pickup and delivery indices and counts
  const { pickupIndices, deliveryIndices, pickupCount, deliveryCount } = useMemo(() => {
    const pickups: number[] = []
    const deliveries: number[] = []
    
    selectedOrder?.orderEvents?.forEach((event, index) => {
      if (event.eventType === 'PICKUP') {
        pickups.push(index)
      } else if (event.eventType === 'DELIVERY') {
        deliveries.push(index)
      }
    })
    
    return {
      pickupIndices: pickups,
      deliveryIndices: deliveries,
      pickupCount: pickups.length,
      deliveryCount: deliveries.length,
    }
  }, [selectedOrder?.orderEvents])

  const hasPickup = pickupCount > 0
  const hasDelivery = deliveryCount > 0

  // Generate dynamic tabs (hide Customer & Rate for dispatch)
  const dynamicTabs = useMemo(() => {
    const baseTabs = [...verifyOrderTabs]
      .filter((tab) => {
        if (isDispatch && (tab.id === 'customer' || tab.id === 'rate')) {
          return false
        }
        return true
      })

    const tabs = [...baseTabs]

    if (pickupCount > 0) {
      const pickupTabIndex = tabs.findIndex((tab) => tab.id === 'pickup')
      if (pickupTabIndex !== -1) {
        tabs.splice(pickupTabIndex, 1)
        pickupIndices.forEach((_, idx) => {
          const label = pickupCount === 1 ? 'Pickup' : `Pickup ${idx + 1}`
          tabs.splice(pickupTabIndex + idx, 0, { id: `pickup${idx + 1}`, label })
        })
      }
    }

    if (deliveryCount > 0) {
      const deliveryTabIndex = tabs.findIndex((tab) => tab.id === 'delivery')
      if (deliveryTabIndex !== -1) {
        tabs.splice(deliveryTabIndex, 1)
        deliveryIndices.forEach((_, idx) => {
          const label = deliveryCount === 1 ? 'Delivery' : `Delivery ${idx + 1}`
          tabs.splice(deliveryTabIndex + idx, 0, { id: `delivery${idx + 1}`, label })
        })
      }
    }

    return tabs
  }, [pickupCount, deliveryCount, pickupIndices, deliveryIndices, isDispatch])

  // Show simple message for processing/failed orders
  const processingMessage = useMemo(() => {
    if (!isPendingLcDoc) return ''
    const fileName = selectedOrder?.fileName || selectedOrder?.customerName || ''
    const isFailedFile = fileName.startsWith('FAILED_')
    if (isFailedFile) {
      return 'Failed. Retry later'
    }
    const displayName = fileName
    return `${displayName} processing`
  }, [isPendingLcDoc, selectedOrder?.fileName, selectedOrder?.customerName])

  if (!selectedOrder || selectedOrder.orderId === 0) {
    return <OrderNotSelected />
  }

  if (isSelectedOrderLoading) {
    return <OrderDetailsSkeleton />
  }

  const footerSx = {
    flexShrink: 0,
    position: 'relative' as const,
    minHeight: 56,
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        flex: '1 1 0%',
        height: '100%',
        minHeight: 0,
      }}
    >
      {selectedOrder?.orderId !== 0 ? (
        <>
          <Box
            sx={{
              flex: '1 1 0%',
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            <FormProvider {...formMethods}>
              <BasicTabs
                tabs={dynamicTabs}
                value={tabsValue}
                handleChange={handleChange}
                fillContent
              >
                <Box
                  sx={{
                    flex: '1 1 0%',
                    minHeight: 0,
                    height: '100%',
                    display: 'grid',
                    gridTemplateColumns: 'minmax(0, 1fr) minmax(260px, 34%)',
                    gridTemplateRows: 'minmax(0, 1fr)',
                    overflow: 'hidden',
                  }}
                >
                  <Box
                    sx={{
                      minWidth: 0,
                      minHeight: 0,
                      overflow: 'auto',
                      overscrollBehaviorY: 'contain',
                      p: 2.5,
                      bgcolor: 'background.paper',
                    }}
                  >
                    {/* Customer & Rate tabs are hidden for dispatch users */}
                    {!isDispatch && (
                      <>
                        <TabPanel value={tabsValue} index={0}>
                          {isPendingLcDoc ? (
                            <Typography variant="h6" fontWeight={700} sx={{ mt: 2 }}>
                              {processingMessage}
                            </Typography>
                          ) : (
                            <LoadCustomerForm displayVariant="orderDetails" />
                          )}
                        </TabPanel>
                        <TabPanel value={tabsValue} index={1}>
                          {isPendingLcDoc ? (
                            <Typography variant="h6" fontWeight={700} sx={{ mt: 2 }}>
                              {processingMessage}
                            </Typography>
                          ) : (
                            <LoadRateForm displayVariant="orderDetails" />
                          )}
                        </TabPanel>
                      </>
                    )}
                    {/* Render pickup tabs */}
                    {hasPickup && (
                      <>
                        {pickupIndices.map((eventIndex, idx) => {
                          // Base index after optional Customer/Rate tabs
                          const baseOffset = isDispatch ? 0 : 2
                          const tabIndex = baseOffset + idx
                          return (
                            <TabPanel key={`pickup-${eventIndex}`} value={tabsValue} index={tabIndex}>
                              {isPendingLcDoc ? (
                                <Typography variant="h6" fontWeight={700} sx={{ mt: 2 }}>
                                  {processingMessage}
                                </Typography>
                              ) : (
                                <LoadPickupForm eventIndex={eventIndex} hideIcons />
                              )}
                            </TabPanel>
                          )
                        })}
                      </>
                    )}
                    {/* Render delivery tabs */}
                    {hasDelivery && (
                      <>
                        {deliveryIndices.map((eventIndex, idx) => {
                          const baseOffset = isDispatch ? 0 : 2
                          const tabIndex = baseOffset + pickupCount + idx
                          return (
                            <TabPanel key={`delivery-${eventIndex}`} value={tabsValue} index={tabIndex}>
                              {isPendingLcDoc ? (
                                <Typography variant="h6" fontWeight={700} sx={{ mt: 2 }}>
                                  {processingMessage}
                                </Typography>
                              ) : (
                                <LoadDeliveryForm eventIndex={eventIndex} hideIcons />
                              )}
                            </TabPanel>
                          )
                        })}
                      </>
                    )}
                    {/* Notes tab - always at the end */}
                    <TabPanel
                      value={tabsValue}
                      index={(isDispatch ? 0 : 2) + pickupCount + deliveryCount}
                    >
                      {isPendingLcDoc ? (
                        <Typography variant="h6" fontWeight={700} sx={{ mt: 2 }}>
                          {processingMessage}
                        </Typography>
                      ) : (
                        <LoadNotesForm />
                      )}
                    </TabPanel>
                  </Box>
                  <Box
                    sx={{
                      minWidth: 0,
                      minHeight: 0,
                      bgcolor: 'secondary.light',
                      borderLeft: '1px solid',
                      borderColor: 'divider',
                      overflow: 'hidden',
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                  >
                    <OrderTracking />
                  </Box>
                </Box>
              </BasicTabs>
            </FormProvider>
          </Box>
          {!isPendingLcDoc &&
            selectedOrderTab === 'ACTIVE' &&
            selectedOrder?.status !== 'ARCHIVED' && (
              <ActionButtons
                containerSx={footerSx}
                firstBtnProps={{
                  title: 'Mark as Completed',
                  onClick: handleMarkAsCompleted,
                }}
              />
            )}
        </>
      ) : (
        <OrderNotSelected />
      )}
      <ConfirmationDialog
        title="Delete Order"
        open={deleteOpen}
        handleClose={() => setDeleteOpen(false)}
        successBtnText="Delete"
        successBtnTextStyle={{ color: 'error' }}
        handleSuccessBtn={handleDeleteOrder}
      >
        Are you sure to delete the order?
      </ConfirmationDialog>
    </Box>
  )
}

const OrderDetails = () => {
  return (
    <EditModeProvider>
      <OrderDetailsContent />
    </EditModeProvider>
  )
}

export default OrderDetails
